import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Flag, Computer, Palette, PenTool, FileText, GraduationCap, Camera, Music, Languages, HeartPulse, Shirt, ShoppingBag, TrendingUp, Gamepad2, Cog, MessageCircle } from 'lucide-react';
import { ServiceDescriptionDialog } from '@/components/ServiceDescriptionDialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/UserAvatar';
import { UserProgressSection } from '@/components/UserProgressSection';
import { ProviderRatings } from '@/components/ProviderRatings';
import { PortfolioGrid } from '@/components/portfolio/PortfolioGrid';
import { usePortfolio } from '@/hooks/usePortfolio';
import { useProfileUpdates } from '@/hooks/useProfileUpdates';
import { ReportUserDialog } from '@/components/ReportUserDialog';
import { ServiceLocationLabel } from '@/components/ServiceLocationLabel';
import { getCachedLocation } from '@/utils/locationUtils';
import { toast } from 'sonner';
import { AvailabilityDisplay, WeeklySchedule } from '@/components/services/AvailabilitySchedule';

// Category icons and colors - copied from DiscoverServices for consistency
const categoryIcons: Record<string, React.FC<{ className?: string }>> = {
  "Web Development": Computer,
  "Design": PenTool,
  "Writing": FileText,
  "Marketing and Sales": TrendingUp,
  "Game Development": Gamepad2,
  "Tutoring": GraduationCap,
  "Photography": Camera,
  "Music": Music,
  "Language": Languages,
  "Health": HeartPulse,
  "Fashion": Shirt,
  "Business": ShoppingBag,
  "Arts": Palette,
  "Engineering and CAD": Cog,
  "Other": Computer
};

const categoryColors: Record<string, string> = {
  "Web Development": "bg-blue-500 text-white",
  "Design": "bg-pink-500 text-white",
  "Writing": "bg-green-500 text-white",
  "Marketing and Sales": "bg-orange-500 text-white",
  "Game Development": "bg-purple-500 text-white",
  "Tutoring": "bg-amber-500 text-white",
  "Photography": "bg-indigo-500 text-white",
  "Music": "bg-red-500 text-white",
  "Language": "bg-emerald-500 text-white",
  "Health": "bg-rose-500 text-white",
  "Fashion": "bg-fuchsia-500 text-white",
  "Business": "bg-cyan-500 text-white",
  "Arts": "bg-violet-500 text-white",
  "Engineering and CAD": "bg-slate-500 text-white",
  "Other": "bg-gray-500 text-white"
};

interface ServiceWithCategory {
  id: string;
  title: string;
  description: string | null;
  price: number;
  pricing_type: string;
  image_url: string | null;
  category_id: string | null;
  user_id: string;
  delivery_method?: string | null;
  service_location?: any;
  availability_schedule?: any;
  service_categories?: {
    id: string;
    name: string;
  } | null;
}

interface UserProfileData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  bio: string | null;
  school: string | null;
  avatar_url: string | null;
}

export default function UserProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [services, setServices] = useState<ServiceWithCategory[]>([]);
  const [serviceLocations, setServiceLocations] = useState<Record<string, string>>({});
  const [serviceConversations, setServiceConversations] = useState<{ serviceId: string; conversationId: string }[]>([]);
  const [requestingService, setRequestingService] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [descriptionDialog, setDescriptionDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
  }>({ isOpen: false, title: "", description: "" });
  const isCurrentUser = user && userId === user.id;

  const { portfolioItems, loading: portfolioLoading } = usePortfolio(userId);
  const { profileVersion } = useProfileUpdates();

  const fetchUserProfile = async () => {
    if (!userId) {
      setError('No user ID specified');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_public_profile', { profile_user_id: userId })
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        setError('Failed to load profile');
        return;
      }
      
      if (!data) {
        setError('Profile not found');
        return;
      }
      
      setProfile(data);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    if (!userId) return;
    
    try {
      setServicesLoading(true);
      const { data, error } = await supabase
        .from('services')
        .select(`
          id, title, description, price, pricing_type, image_url, category_id, user_id,
          delivery_method, service_location,
          service_categories (id, name)
        `)
        .eq('user_id', userId);
      
      if (error) {
        console.error('Error fetching services:', error);
        return;
      }
      
      setServices(data || []);
      
      // Load location names
      const locations: Record<string, string> = {};
      for (const service of data || []) {
        if (service.service_location && service.delivery_method !== "virtual") {
          try {
            const loc = service.service_location as { latitude: number; longitude: number; radius: number };
            const locationName = await getCachedLocation(loc.latitude, loc.longitude);
            locations[service.id] = locationName;
          } catch (err) {
            console.error(`Error loading location for service ${service.id}:`, err);
          }
        }
      }
      setServiceLocations(locations);
    } catch (err) {
      console.error('Unexpected error fetching services:', err);
    } finally {
      setServicesLoading(false);
    }
  };

  // Load existing conversations for services
  useEffect(() => {
    const loadServiceConversations = async () => {
      if (!user || !userId) return;
      
      try {
        const { data, error } = await supabase
          .from('conversations')
          .select('id, service_id')
          .not('service_id', 'is', null)
          .eq('service_completed', false)
          .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`);
        
        if (error) {
          console.error('Error loading service conversations:', error);
          return;
        }
        
        if (data) {
          const conversations = data
            .map(item => ({
              serviceId: item.service_id as string,
              conversationId: item.id as string
            }))
            .filter(item => item.serviceId && item.conversationId);
          setServiceConversations(conversations);
        }
      } catch (err) {
        console.error('Error fetching service conversations:', err);
      }
    };
    loadServiceConversations();
  }, [user, userId]);

  useEffect(() => {
    fetchUserProfile();
    fetchServices();
  }, [userId, profileVersion]);

  const getConversationForService = (serviceId: string): string | null => {
    const conversation = serviceConversations.find(sc => sc.serviceId === serviceId);
    return conversation ? conversation.conversationId : null;
  };

  const handleRequestService = async (service: ServiceWithCategory) => {
    if (!user) {
      toast.info("Please sign in to request this service");
      navigate("/auth?mode=login&redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    if (service.user_id === user.id) {
      toast.error("You cannot request your own service");
      return;
    }
    setRequestingService(service.id);
    try {
      const existingConvo = getConversationForService(service.id);
      if (existingConvo) {
        navigate(`/chat/${existingConvo}`);
        return;
      }

      const initialMessage = `Hi, I'm interested in your ${service.title} service.`;

      const { data: newConvo, error: convoError } = await supabase
        .from('conversations')
        .insert({
          participant1_id: user.id,
          participant2_id: service.user_id,
          service_id: service.id,
          last_message: initialMessage
        })
        .select('id')
        .single();

      if (convoError || !newConvo) {
        throw new Error("Failed to create conversation");
      }

      const { error: msgError } = await supabase
        .from('messages')
        .insert({
          conversation_id: newConvo.id,
          sender_id: user.id,
          text: initialMessage
        });

      if (msgError) {
        throw new Error("Failed to send initial message");
      }

      setServiceConversations(prev => [...prev, {
        serviceId: service.id,
        conversationId: newConvo.id
      }]);
      toast.success("Service requested! You can now chat with the provider.");
      navigate(`/chat/${newConvo.id}`);
    } catch (err) {
      console.error("Error requesting service:", err);
      toast.error("Failed to request service. Please try again.");
    } finally {
      setRequestingService(null);
    }
  };

  const getFullName = () => {
    if (!profile) return 'Unknown User';
    if (profile.first_name && profile.last_name) return `${profile.first_name} ${profile.last_name}`;
    if (profile.first_name) return profile.first_name;
    return 'Unknown User';
  };

  const getInitials = () => {
    if (!profile) return 'UP';
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    if (profile.first_name) {
      return profile.first_name[0].toUpperCase();
    }
    return 'UP';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-10">
        <Skeleton className="h-12 w-64 mb-6" />
        <div className="grid md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-40" />
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center space-y-4">
                <Skeleton className="h-24 w-24 rounded-full" />
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-20 w-full" />
              </div>
            </CardContent>
          </Card>
          <div className="md:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error || 'Provider not found'}</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-muted-foreground hover:text-anyskill-purple mr-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
          <h1 className="font-poppins font-bold text-3xl">{getFullName()}</h1>
          {isCurrentUser && (
            <Link to="/my-profile" className="ml-4 text-blue-600 hover:text-blue-800 text-sm">
              Edit Profile
            </Link>
          )}
        </div>
        
        {user && !isCurrentUser && userId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReportDialogOpen(true)}
            className="text-muted-foreground hover:text-destructive text-xs"
          >
            <Flag className="h-3 w-3 mr-1" />
            Report
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Profile Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>About</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <UserAvatar
                userId={userId}
                avatarUrl={profile?.avatar_url}
                fallback={getInitials()}
                size="xl"
                showLevel={true}
              />
              <h2 className="text-xl font-semibold">{getFullName()}</h2>
              
              {(profile?.school) && (
                <div className="text-sm text-muted-foreground">
                  {profile.school && <div>{profile.school}</div>}
                </div>
              )}
              
              {profile?.bio && (
                <div className="mt-4 text-foreground">
                  <p>{profile.bio}</p>
                </div>
              )}
              
              {!profile?.bio && (
                <div className="mt-4 text-muted-foreground italic">
                  <p>No bio provided</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Services, Portfolio, and Progress */}
        <div className="md:col-span-2 space-y-6">
          {/* Services Section */}
          <Card>
            <CardHeader>
              <CardTitle>Services Offered</CardTitle>
            </CardHeader>
            <CardContent>
              {servicesLoading ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading services...</p>
                </div>
              ) : services.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No services offered yet</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services.map((service) => {
                    const categoryName = service.service_categories?.name || "Uncategorized";
                    const conversationId = getConversationForService(service.id);
                    const isOwnService = user && service.user_id === user.id;
                    const locationName = serviceLocations[service.id];
                    const IconComponent = categoryIcons[categoryName] || Computer;
                    const colorClass = categoryColors[categoryName] || "bg-gray-500 text-white";
                    
                    return (
                      <Card key={service.id} className="overflow-hidden hover:shadow-md transition-shadow relative border-2 border-green-200">
                        {/* Service Badge */}
                        <div className="absolute top-2 left-2 z-10">
                          <Badge variant="secondary" className="bg-green-500 text-white text-xs font-medium px-2 py-1">
                            SERVICE
                          </Badge>
                        </div>

                        <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
                          {service.image_url ? (
                            <>
                              <img src={service.image_url} alt={service.title} className="w-full h-full object-contain bg-gray-50 dark:bg-gray-800" />
                              <div className="absolute top-2 right-2">
                                <div className={`${colorClass} p-1.5 rounded-full shadow-lg border-2 border-white backdrop-blur-sm bg-opacity-90`}>
                                  <IconComponent className="h-4 w-4" />
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className={`${colorClass} p-2 rounded-full`}>
                                <IconComponent className="h-6 w-6" />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <CardContent className="p-4">
                          <CardTitle className="text-base mb-3 line-clamp-2 leading-tight">{service.title}</CardTitle>
                          <p 
                            className="text-sm text-muted-foreground mb-3 line-clamp-3 cursor-pointer hover:text-foreground transition-colors"
                            onClick={() => setDescriptionDialog({
                              isOpen: true,
                              title: service.title,
                              description: service.description || "No description provided."
                            })}
                            title="Click to read full description"
                          >
                            {service.description || "No description provided."}
                          </p>
                          
                          <ServiceLocationLabel deliveryMethod={service.delivery_method as "virtual" | "in-person" | "both" | undefined} location={locationName} className="mb-2 text-xs" />
                          
                          {service.availability_schedule && service.delivery_method !== "virtual" && (
                            <div className="mb-3">
                              <AvailabilityDisplay schedule={service.availability_schedule as WeeklySchedule} />
                            </div>
                          )}
                          
                          <div className="flex justify-between items-end">
                            <div className="flex flex-col">
                              <div className="flex items-center">
                                <span className="font-semibold text-base">${service.price}</span>
                                {service.pricing_type && (
                                  <span className="text-sm text-muted-foreground ml-1">
                                    {service.pricing_type === "hourly" ? "/hr" : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex gap-2 flex-wrap">
                              {!isOwnService && (
                                user ? (
                                  conversationId ? (
                                    <Link to={`/chat/${conversationId}`}>
                                      <Button size="sm" variant="outline" className="flex items-center gap-1 text-xs px-3 py-2 h-8">
                                        <MessageCircle size={14} />
                                        Chat
                                      </Button>
                                    </Link>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      onClick={(e) => { e.preventDefault(); handleRequestService(service); }} 
                                      disabled={requestingService === service.id} 
                                      className="text-xs px-3 py-2 h-8"
                                    >
                                      {requestingService === service.id ? "Requesting..." : "Request"}
                                    </Button>
                                  )
                                ) : (
                                  <Button 
                                    size="sm" 
                                    onClick={(e) => { e.preventDefault(); handleRequestService(service); }} 
                                    className="text-xs px-3 py-2 h-8"
                                  >
                                    Request
                                  </Button>
                                )
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Portfolio Section */}
          <Card>
            <CardHeader>
              <CardTitle>Portfolio</CardTitle>
            </CardHeader>
            <CardContent>
              {portfolioLoading ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading portfolio...</p>
                </div>
              ) : (
                <PortfolioGrid
                  items={portfolioItems}
                  isOwner={false}
                />
              )}
            </CardContent>
          </Card>

          {/* Ratings and Reviews */}
          {userId && <ProviderRatings providerId={userId} maxReviews={5} />}

          {/* Progress Section */}
          {userId && (
            <UserProgressSection userId={userId} />
          )}
        </div>
      </div>

      {/* Report Dialog */}
      {userId && (
        <ReportUserDialog
          isOpen={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          reportedUserId={userId}
          reportedUserName={getFullName()}
        />
      )}
      
      <ServiceDescriptionDialog
        isOpen={descriptionDialog.isOpen}
        onClose={() => setDescriptionDialog({ isOpen: false, title: "", description: "" })}
        title={descriptionDialog.title}
        description={descriptionDialog.description}
      />
    </div>
  );
}
